import { getFacilitiesMarginQueryOptions } from '@/entities/facility/api/get-facilities-margin.query';
import { getNormalizedError } from '@/shared/api/lib';
import { FocusSummaryCard } from '@/shared/components';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { shouldShowSparkline } from '@/shared/lib/sparkline-range';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type MarginCardProps = {
  withChart?: boolean;
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

export const MarginCard: FC<MarginCardProps> = ({
  withChart = true,
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const { data, isLoading, isFetching, error } = useQuery(
    getFacilitiesMarginQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  const daysDifference = dateRange.to.diff(dateRange.from, 'days');
  const showSparkline = shouldShowSparkline(dateRange);

  if (daysDifference < 27) {
    return (
      <FocusSummaryCard
        valueAsComment
        icon="margin"
        title="Margin"
        tooltipMessage="Calculated as: (Revenue − Payroll Expenses) ÷ Revenue × 100"
        value="Margin data is available only for date ranges of one month or longer."
        loading={false}
      />
    );
  }

  const isForbidden = getNormalizedError(error).statusCode === 422;

  if (isForbidden) {
    return (
      <FocusSummaryCard
        valueAsComment
        icon="margin"
        title="Margin"
        value="Please enter staff salaries on the platform to see this metric"
        tooltipMessage="Calculated as: (Revenue − Payroll Expenses) ÷ Revenue × 100"
        loading={false}
      />
    );
  }

  if (!isLoading && !data) {
    return (
      <FocusSummaryCard
        value="N/A"
        icon="margin"
        title="Margin"
        loading={false}
      />
    );
  }

  const indicator =
    data?.margin.changePct != null
      ? {
          value: data.margin.changePct,
          negativeValue: data.margin.changePct < 0,
        }
      : undefined;

  const chartData = {
    data: {
      labels:
        data?.sparkline?.points?.map((_, index) => index.toString()) ?? [],
      datasets: [
        {
          data: data?.sparkline?.points?.map((point) => point.value) ?? [],
          borderColor: '#165BAA',
          backgroundColor: 'transparent',
          fill: false,
        },
      ],
    },
  };

  return (
    <FocusSummaryCard
      icon="margin"
      title="Margin"
      tooltipMessage="Calculated as: (Revenue − Payroll Expenses) ÷ Revenue × 100"
      indicator={indicator}
      showChart={showSparkline}
      chart={withChart && showSparkline ? chartData : undefined}
      value={
        data?.margin.currentPct != null ? `${data.margin.currentPct}%` : 'N/A'
      }
      loading={isFetching}
    />
  );
};
